import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import productLabels from 'i18n/productLabels'
import formatStaticAppText from 'lib/format/formatStaticAppText'

/**
 * Localizes navigation item labels using react-intl.
 *
 * All message descriptors live inside `intl.formatMessage()` calls so the
 * formatjs extractor can discover them and they execute within React intl context.
 *
 * Items whose id matches a known entry are translated first, then a shared
 * fallback covers config-driven labels such as brand, destination, and event
 * names that are still stored as plain strings.
 */
function useLocalizedNavItems(navItems: Array<App.NavItem>): Array<App.NavItem> {
  const intl = useIntl()

  return useMemo(() => {
    const localizedTextById: Record<string, string> = {
      'nav-link-hotels': intl.formatMessage(productLabels.hotels),
      'hotelsandresorts': intl.formatMessage(productLabels.hotelsAndResorts),
      'nav-link-homes-and-villas': intl.formatMessage({
        id: '8LeYyKFJg8MybzX0_uru',
        defaultMessage: 'Homes & Villas',
        description: 'Navigation link label for homes and villas on the rental page',
      }),
      'nav-link-tours': intl.formatMessage(productLabels.tours),
      'nav-link-tours-cruises': intl.formatMessage({
        id: 'xQps7CG9POHZCcMywF0T',
        defaultMessage: 'Tours & Cruises',
        description: 'Navigation link label for tours and cruises in the cruise section',
      }),
      'nav-link-cruises': intl.formatMessage({
        id: 'zZtVcxihXkAuNc6yzggI',
        defaultMessage: 'Cruises',
        description: 'Navigation link label for cruises in the cruise offer or booking section',
      }),
      'nav-link-flights': intl.formatMessage({
        id: 'krMvDAr0uYbCeGc7wim-',
        defaultMessage: 'Flights',
        description: 'Navigation link label for flights in the flight search or booking flow',
      }),
      'nav-link-car-hire': intl.formatMessage({
        id: 'eN5d8Ihn9EN5S1i1T5P0',
        defaultMessage: 'Car Hire',
        description: 'Navigation link label for car hire in the car hire booking section',
      }),
      'nav-link-ultralux': intl.formatMessage(productLabels.ultraLux),
      'nav-link-experiences': intl.formatMessage({
        id: 'xniFVpzkLvn62hnz91eg',
        defaultMessage: 'Experiences',
        description: 'Navigation link label for experiences on the experience offer or booking page',
      }),
      'nav-link-maldives-packages': intl.formatMessage({
        id: 'TVTE2GfMvVGYatqsxsQV',
        defaultMessage: 'Maldives Bundles',
        description: 'Navigation link label for Maldives bundles for computed display labels',
      }),
      'nav-link-friends-and-family': intl.formatMessage({
        defaultMessage: 'Friends & Family Discount',
        id: 'THwlmy6qdU3W1PmWG123',
        description: 'Navigation link label for the friends and family discount invite page',
      }),
      'nav-link-inspiration': intl.formatMessage({
        id: 'L5tPNRc9zuw4iSOi6B7u',
        defaultMessage: 'Inspiration',
        description: 'Navigation link label for inspiration for computed display labels',
      }),
      'nav-link-support': intl.formatMessage({
        id: 'B-qwt8A--qbvDgXthp5w',
        defaultMessage: 'Support',
        description: 'Navigation link label for support in the customer support section',
      }),
      'nav-link-store': intl.formatMessage({
        id: 'FBFpURkKBNJZYymS62H3',
        defaultMessage: 'Visit Us In Store',
        description: 'Navigation link label for store visit for computed display labels',
      }),
      'nav-link-gift-cards': intl.formatMessage({
        id: 'dn4C1HGznaDx69RdhcpC',
        defaultMessage: 'Gift Cards',
        description: 'Navigation link label for gift cards on the gift card purchase or redemption page',
      }),
      'nav-link-insurance': intl.formatMessage({
        id: '0uCIrvaDC7GuNC20l_2x',
        defaultMessage: 'Travel Protection',
        description: 'Navigation link label for travel protection in the insurance flow',
      }),
      'nav-link-marketplace': intl.formatMessage({
        id: 'yj4Tt2PRMLWLQbrIsa-t',
        defaultMessage: 'Shop',
        description: 'Navigation link label for shop/marketplace for computed display labels',
      }),
      'nav-link-refer': intl.formatMessage({
        id: 'di1TiTwCs8I9qir-h5Bf',
        defaultMessage: 'Refer a Friend',
        description: 'Navigation link label for refer a friend for computed display labels',
      }),
      'nav-link-le4b': intl.formatMessage({
        id: 'r0nhk6dhtQ7Apc6QnEIr',
        defaultMessage: 'Business Travel',
        description: 'Navigation link label for business travel for computed display labels',
      }),
      'nav-link-golf': intl.formatMessage({
        id: 'I9Wq2BBPti9KZKSMP-E-',
        defaultMessage: 'Golf',
        description: 'Navigation link label for golf for computed display labels',
      }),
      'nav-link-groups': intl.formatMessage({
        id: 'RH3MYjnXMjRZYRhhjUen',
        defaultMessage: 'Groups',
        description: 'Navigation link label for groups for computed display labels',
      }),
      'nav-link-weddings': intl.formatMessage({
        id: 'kzC5Y7pk5hj3tGpXIiOf',
        defaultMessage: 'Weddings',
        description: 'Navigation link label for weddings for computed display labels',
      }),
      'nav-link-more': intl.formatMessage({
        id: 'fzR_C3wS9Ain-aQheyoM',
        defaultMessage: 'More',
        description: 'Navigation link label for the more dropdown for computed display labels',
      }),
    }

    if (isAgentHubEnabled) {
      localizedTextById['nav-link-flights'] = intl.formatMessage({
        id: 'vRx2ckV-2zHsxgYhn8dq',
        defaultMessage: 'Direct Maldives Flights',
        description: 'Navigation link label for direct Maldives flights for computed display labels',
      })
    }

    const localizedFromConfigString = (raw: string) => {
      const descriptor = Object.values(productLabels).find(d => d.defaultMessage === raw)
      return descriptor ? intl.formatMessage(descriptor) : formatStaticAppText(intl, raw)
    }

    const localize = (item: App.NavItem): App.NavItem => {
      let text: string | undefined
      if (item.id) {
        // Both "Tours" and "Tours & Cruises" share id "nav-link-tours" in
        // navItemsByRegion. Disambiguate using the source text so each gets
        // its own translation.
        if (item.id === 'nav-link-tours' && item.text === 'Tours & Cruises') {
          text = localizedTextById['nav-link-tours-cruises']
        } else {
          text = localizedTextById[item.id]
        }
      }

      const localizedText = text ?? localizedFromConfigString(item.text)
      const localizedItems = item.items?.map(localize)

      if (localizedText === item.text && !localizedItems?.length) return item

      return {
        ...item,
        text: localizedText,
        items: localizedItems,
      }
    }

    return navItems.map(localize)
  }, [navItems, intl])
}

export default useLocalizedNavItems
